import React, { useState, useEffect } from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Button,
  IconButton,
  Chip,
  Avatar,
  CircularProgress,
  Alert,
  Menu,
  MenuItem,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
} from '@mui/material';
import {
  Timeline,
  TimelineItem,
  TimelineSeparator,
  TimelineConnector,
  TimelineContent,
  // TimelineDot,
} from '@mui/lab';
import {
  Download as DownloadIcon,
  Restore as RestoreIcon,
  Compare as CompareIcon,
  MoreVert as MoreVertIcon,
  // CloudDownload as CloudDownloadIcon,
  History as HistoryIcon,
  Analytics as AnalyticsIcon,
} from '@mui/icons-material';
import { useTranslation } from 'react-i18next';
import { formatDateSafe } from '../../utils/dateUtils';

// Get API URL from environment or use current origin
const API_URL = process.env.REACT_APP_API_URL || window.location.origin;

interface AnalysisEntry {
  id: string;
  type: string;
  content: string;
  created_at: string;
  created_by: string;
  version: number;
}

interface DocumentVersion {
  id: string;
  versionNumber: number;
  fileName: string;
  fileSize: number;
  uploadedBy: string;
  uploadedAt: string;
  changesSummary?: string;
  isCurrentVersion: boolean;
  downloadUrl?: string;
  relatedAnalysis?: AnalysisEntry[]; // 関連する分析履歴
}

interface VersionHistoryViewProps {
  documentId?: string;
  analysisHistory?: AnalysisEntry[];
}

const VersionHistoryView: React.FC<VersionHistoryViewProps> = ({ documentId, analysisHistory = [] }) => {
  const { t } = useTranslation();
  const [versions, setVersions] = useState<DocumentVersion[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const [selectedVersion, setSelectedVersion] = useState<DocumentVersion | null>(null);
  const [compareDialogOpen, setCompareDialogOpen] = useState(false);
  const [compareVersions, setCompareVersions] = useState<{from: DocumentVersion | null, to: DocumentVersion | null}>({
    from: null,
    to: null
  });

  useEffect(() => {
    const fetchVersionHistory = async () => {
      if (!documentId) return;

      try {
        setLoading(true);
        setError(null);

        // Get authentication token
        const token = sessionStorage.getItem('access_token');

        // Fetch actual version history from API with authentication
        const response = await fetch(`${API_URL}/api/v1/documents/${documentId}/versions`, {
          headers: token ? { 'Authorization': `Bearer ${token}` } : {},
        });
        if (!response.ok) {
          if (response.status === 401) {
            throw new Error(t('errors.authenticationRequired'));
          }
          throw new Error(t('documents.versionHistoryFetchFailed'));
        }

        const data = await response.json();
        const apiVersions = data.versions || [];
        const currentVersionNumber = data.currentVersion || 1;

        // Transform API response to match component interface
        const transformedVersions: DocumentVersion[] = apiVersions.map((v: any) => {
          // Find related analysis that happened after this version was uploaded
          const versionUploadTime = new Date(v.uploadedAt);
          const relatedAnalysis = analysisHistory.filter(analysis => {
            const analysisTime = new Date(analysis.created_at);
            return analysisTime >= versionUploadTime;
          });

          return {
            id: `v${v.versionNumber}`,
            versionNumber: v.versionNumber,
            fileName: v.fileName,
            fileSize: v.fileSize,
            uploadedBy: v.uploadedBy || t('common.unknown'),
            uploadedAt: v.uploadedAt,
            changesSummary: v.changeLog || t('documents.noDescription'),
            isCurrentVersion: v.versionNumber === currentVersionNumber,
            downloadUrl: v.path,
            relatedAnalysis: relatedAnalysis
          };
        });

        // Sort versions by version number descending
        transformedVersions.sort((a, b) => b.versionNumber - a.versionNumber);

        setVersions(transformedVersions);
        setLoading(false);
      } catch (err) {
        const errorMessage = (err as Error).message;

        // Check if this is an authentication error - don't fall back to mock data
        if (errorMessage.includes('認証') || errorMessage.includes('401') || errorMessage.includes('authentication')) {
          console.error('Authentication error loading version history:', errorMessage);
          setError(errorMessage);
          setLoading(false);
          return;
        }

        console.warn('Version history API not available, using fallback data');

        // Fallback: Create mock version data with analysis if available
        if (analysisHistory.length > 0) {
          const mockVersions: DocumentVersion[] = [
            {
              id: 'v1',
              versionNumber: 1,
              fileName: 'Document.pdf',
              fileSize: 762560,
              uploadedBy: 'test-user',
              uploadedAt: '2025-08-27T21:28:53.642234',
              changesSummary: 'Initial upload',
              isCurrentVersion: true,
              downloadUrl: '',
              relatedAnalysis: analysisHistory // 全ての分析を表示
            }
          ];
          setVersions(mockVersions);
        } else {
          setError(t('documents.versionHistoryLoadFailed'));
        }
        setLoading(false);
      }
    };

    if (documentId) {
      fetchVersionHistory();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps -- t only builds the load-error message; excluded so a language change doesn't refetch version history (PR #790)
  }, [documentId, analysisHistory]);

  const formatFileSize = (bytes: number): string => {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
  };

  const handleMenuClick = (event: React.MouseEvent<HTMLElement>, version: DocumentVersion) => {
    setAnchorEl(event.currentTarget);
    setSelectedVersion(version);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
    setSelectedVersion(null);
  };

  const handleDownload = async (version: DocumentVersion) => {
    if (!documentId) return;

    try {
      // Download the specific version (currently downloads the latest)
      const response = await fetch(`${API_URL}/api/v1/documents/${documentId}/download`);
      if (response.ok) {
        const blob = await response.blob();
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = version.fileName;
        document.body.appendChild(a);
        a.click();
        window.URL.revokeObjectURL(url);
        document.body.removeChild(a);
      }
    } catch (err) {
      console.error('Download failed:', err);
    }
    handleMenuClose();
  };

  const handleRestore = (version: DocumentVersion) => {
    // Implement restore logic
    console.log('Restore to version:', version.id);
    handleMenuClose();
  };

  const handleCompare = () => {
    if (selectedVersion) {
      const currentVersion = versions.find(v => v.isCurrentVersion);
      setCompareVersions({
        from: selectedVersion,
        to: currentVersion || null
      });
      setCompareDialogOpen(true);
    }
    handleMenuClose();
  };

  const handleCompareDialogClose = () => {
    setCompareDialogOpen(false);
    setCompareVersions({ from: null, to: null });
  };

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" p={4}>
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return (
      <Alert severity="error">
        {error}
      </Alert>
    );
  }

  if (versions.length === 0) {
    return (
      <Box textAlign="center" p={4}>
        <HistoryIcon sx={{ fontSize: 48, color: 'text.secondary', mb: 2 }} />
        <Typography variant="body1" color="text.secondary">
          {t('documents.noVersionHistory')}
        </Typography>
      </Box>
    );
  }

  return (
    <Box>
      <Timeline position="alternate">
        {versions.map((version, index) => (
          <TimelineItem key={version.id}>
            <TimelineSeparator>
              {/* <TimelineDot color={version.isCurrentVersion ? 'primary' : 'grey'}>
                {version.isCurrentVersion ? (
                  <CloudDownloadIcon />
                ) : (
                  <HistoryIcon />
                )}
              </TimelineDot> */}
              {index < versions.length - 1 && <TimelineConnector />}
            </TimelineSeparator>
            <TimelineContent>
              <Card sx={{ maxWidth: 400, mb: 2 }}>
                <CardContent>
                  <Box display="flex" justifyContent="space-between" alignItems="flex-start" mb={2}>
                    <Box>
                      <Typography variant="h6" component="div">
                        {t('documents.versionWithNumber', { version: version.versionNumber })}
                        {version.isCurrentVersion && (
                          <Chip
                            label={t('documents.currentVersion')}
                            size="small"
                            color="primary"
                            sx={{ ml: 1 }}
                          />
                        )}
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        {version.fileName}
                      </Typography>
                    </Box>
                    <IconButton
                      size="small"
                      onClick={(e) => handleMenuClick(e, version)}
                    >
                      <MoreVertIcon />
                    </IconButton>
                  </Box>

                  <Box display="flex" alignItems="center" gap={1} mb={2}>
                    <Avatar sx={{ width: 24, height: 24, fontSize: '0.75rem' }}>
                      {version.uploadedBy.split(' ').map(n => n[0]).join('')}
                    </Avatar>
                    <Typography variant="body2" color="text.secondary">
                      {version.uploadedBy}
                    </Typography>
                  </Box>

                  <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                    {formatDateSafe(version.uploadedAt, 'yyyy/MM/dd HH:mm')}
                  </Typography>

                  <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                    {formatFileSize(version.fileSize)}
                  </Typography>

                  {version.changesSummary && (
                    <Typography variant="body2" sx={{ mb: 2 }}>
                      {version.changesSummary}
                    </Typography>
                  )}

                  {/* 分析履歴の表示 */}
                  {version.relatedAnalysis && version.relatedAnalysis.length > 0 && (
                    <Box sx={{ mb: 2, p: 1, bgcolor: 'action.hover', borderRadius: 1 }}>
                      <Box display="flex" alignItems="center" gap={0.5} mb={1}>
                        <AnalyticsIcon sx={{ fontSize: 16, color: 'primary.main' }} />
                        <Typography variant="caption" color="primary.main" fontWeight="bold">
                          {t('documents.deepAnalysisCompleted')}
                        </Typography>
                      </Box>
                      {version.relatedAnalysis.map((analysis, idx) => (
                        <Box key={analysis.id} sx={{ mb: idx < version.relatedAnalysis!.length - 1 ? 1 : 0 }}>
                          <Box display="flex" alignItems="center" gap={1}>
                            <Chip
                              label={analysis.type === 'deep_technical_analysis'
                                ? t('documents.deepTechnicalAnalysis')
                                : analysis.type}
                              size="small"
                              color="secondary"
                              sx={{ fontSize: '0.7rem', height: '20px' }}
                            />
                            <Typography variant="caption" color="text.secondary">
                              {formatDateSafe(analysis.created_at, 'yyyy/MM/dd HH:mm')}
                            </Typography>
                          </Box>
                          <Typography variant="caption" color="text.secondary" display="block">
                            {t('documents.createdBy', { name: analysis.created_by })}
                          </Typography>
                        </Box>
                      ))}
                    </Box>
                  )}

                  <Box display="flex" gap={1}>
                    <Button
                      size="small"
                      startIcon={<DownloadIcon />}
                      onClick={() => handleDownload(version)}
                    >
                      {t('common.download')}
                    </Button>
                    {!version.isCurrentVersion && (
                      <>
                        <Button
                          size="small"
                          startIcon={<CompareIcon />}
                          onClick={() => {
                            setSelectedVersion(version);
                            handleCompare();
                          }}
                        >
                          {t('documents.compare')}
                        </Button>
                        <Button
                          size="small"
                          startIcon={<RestoreIcon />}
                          onClick={() => handleRestore(version)}
                        >
                          {t('documents.restore')}
                        </Button>
                      </>
                    )}
                  </Box>
                </CardContent>
              </Card>
            </TimelineContent>
          </TimelineItem>
        ))}
      </Timeline>

      {/* Context Menu */}
      <Menu
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        onClose={handleMenuClose}
      >
        <MenuItem onClick={() => selectedVersion && handleDownload(selectedVersion)}>
          <DownloadIcon sx={{ mr: 1 }} />
          {t('common.download')}
        </MenuItem>
        {selectedVersion && !selectedVersion.isCurrentVersion && (
          <>
            <MenuItem onClick={handleCompare}>
              <CompareIcon sx={{ mr: 1 }} />
              {t('documents.compareWithCurrent')}
            </MenuItem>
            <MenuItem onClick={() => selectedVersion && handleRestore(selectedVersion)}>
              <RestoreIcon sx={{ mr: 1 }} />
              {t('documents.restoreThisVersion')}
            </MenuItem>
          </>
        )}
      </Menu>

      {/* Compare Versions Dialog */}
      <Dialog
        open={compareDialogOpen}
        onClose={handleCompareDialogClose}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>
          {t('documents.compareVersions')}
        </DialogTitle>
        <DialogContent>
          <Box display="flex" justifyContent="space-between" gap={2}>
            <Box flex={1}>
              <Typography variant="subtitle1" gutterBottom>
                {t('documents.versionWithNumber', { version: compareVersions.from?.versionNumber })}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                {compareVersions.from?.fileName}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                {compareVersions.from && formatDateSafe(compareVersions.from.uploadedAt, 'yyyy/MM/dd HH:mm')}
              </Typography>
            </Box>
            <Typography variant="h6" color="text.secondary" alignSelf="center">
              {t('documents.versus')}
            </Typography>
            <Box flex={1} textAlign="right">
              <Typography variant="subtitle1" gutterBottom>
                {t('documents.versionWithCurrent', { version: compareVersions.to?.versionNumber })}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                {compareVersions.to?.fileName}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                {compareVersions.to && formatDateSafe(compareVersions.to.uploadedAt, 'yyyy/MM/dd HH:mm')}
              </Typography>
            </Box>
          </Box>
          <Box
            sx={{
              mt: 3,
              p: 2,
              border: '1px dashed',
              borderColor: 'divider',
              borderRadius: 1,
              textAlign: 'center',
            }}
          >
            <Typography variant="body2" color="text.secondary">
              {t('documents.versionComparisonComingSoon')}
            </Typography>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCompareDialogClose}>{t('common.close')}</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default VersionHistoryView;
